import { useWidth } from "@hooks/useWidth";
import { type SpringValue, useSprings } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import type { ReactDOMAttributes } from "@use-gesture/react/dist/declarations/src/types";
import { useCallback, useEffect, useRef, useState } from "react";

const MAX_SWIPE_TRESHOLD = 300;

/**
 * Used to reset springs to initial position
 * @param api ReactSpring ref
 * @param slideWidth slide width in px
 * @param index index of current slide
 */
const useResetSprings = (api: any, slideWidth: number, index: number) => {
    const resetSprings = useCallback(() => {
        if(!slideWidth) return
        api.start((i: number) => ({
            x: ((i - 1) * slideWidth),
            immediate: true,
        }))
    }, [api, slideWidth]);

    // because slideWidth is 0 on spring initialization
    useEffect(resetSprings, [slideWidth, index]);
}

export const useSlider = (
    size: number,
    slideRef: React.RefObject<HTMLElement>,
    defaultIndex: number = 0,
    useWindowForSwipeTreshould: boolean = false,
): {
    index: number,
    slides: {
        index: number,
        x: SpringValue<number>,
        isCurrent: boolean,
        isPrevious: boolean,
        isNext: boolean,
    }[],
    gesturesProps: (...args: any[]) => ReactDOMAttributes,
    handleNext: () => void,
    handlePrevious: () => void,
} => {
    const [index, setIndex] = useState(defaultIndex);
    const { width: slideWidth } = useWidth(slideRef, 0);

    const [props, api] = useSprings(3, i => ({
        x: ((i - 1) * slideWidth),
    }));

    useResetSprings(api, slideWidth, index);

    const animateSlide = useCallback((move: number) => {
        return api.start(i => ({
            x: ((i - 1 - move) * slideWidth),
        }))
    }, [api, slideWidth]);


    const isAnimationInProgess = useRef(false);
    const handleSlide = useCallback((move: number) => {
        if(isAnimationInProgess.current) return;
        isAnimationInProgess.current = true;
        const animationQueue = animateSlide(move);
        Promise.all(animationQueue).finally(() => {
            isAnimationInProgess.current = false;
            setIndex((state: number) => (state + move + size) % size);
        });
    }, [size, animateSlide, setIndex]);

    const handleNext = useCallback(() => {
        handleSlide(1);
    }, [handleSlide]);
    const handlePrevious = useCallback(() => {
        handleSlide(-1);
    }, [handleSlide]);

    const swipeTreshold = useRef<number>(MAX_SWIPE_TRESHOLD);
    useEffect(() => {
        if (!useWindowForSwipeTreshould) {
            swipeTreshold.current = (Math.min(slideWidth * 2 / 5, MAX_SWIPE_TRESHOLD));
            return;
        };
        const updateSwipeTreshold = () => {
            swipeTreshold.current = (Math.min(window.innerWidth * 2 / 6, MAX_SWIPE_TRESHOLD));
        }
        window.addEventListener("resize", updateSwipeTreshold);
        updateSwipeTreshold();
        return () => {
            window.removeEventListener("resize", updateSwipeTreshold);
        }
    }, [slideWidth]);
  
    const gesturesProps = useDrag(
        ({ active, down, movement: [mx], direction: [xDir], cancel }) => {
            let updateIndex = false;
            if (down) {
                if(Math.abs(mx) > swipeTreshold.current) {
                    cancel();
                    updateIndex = true;
                }
            }
            const animationQueue = api.start(i => ({
                x: updateIndex ? ((i + xDir - 1) * slideWidth) : (down ? mx : 0) + ((i-1) * slideWidth),
            }));
            if(updateIndex && !active) {
                Promise.all(animationQueue).finally(() => {
                    setIndex((state: number) => (state - xDir + size) % size);
                })
            }
        },
        {
            axis: 'x'
        }
    );

    const slides = props.map(({ x }, i) => ({
        x,
        index: (size + i - 1 + index) % size,
        isPrevious: i === 0,
        isNext: i == 2,
        isCurrent: i === 1,
    }))


    return {
        index,
        slides,
        gesturesProps,
        handleNext,
        handlePrevious,
    }
}